(() => {
  'use strict';

  const DEFAULTS = {
    pairLabel: 'Pair {number}', printLabel: 'Print', sizeLabel: 'Size',
    addThird: 'Add a third pair for 15% off', addAnother: 'Add another pair · keep 15% off',
    addThirdSaving: 'Add a third pair · save {saving} total at 15%',
    addNextSaving: 'Add another pair · save {saving} total at 15%',
    completePairs: 'Complete your pairs to add another', removePair: 'Remove pair {number}',
    maximumPairs: 'Add more pairs from your cart', noMoreStock: 'No more sizes available to add',
    orderHeading: 'Estimated order totals', eligibleHeading: 'Estimated swim-short totals',
    orderNote: 'Includes eligible shorts already in your cart and these selected pairs. Merchandise estimate only; shipping and final discounts are confirmed in your cart.',
    excludedNote: 'Other items in your cart are excluded from this estimate.',
    orderEstimateUnavailable: 'The full estimate is unavailable. Your cart confirms the final discount and total.',
    choosePrint: 'Choose a print', chooseSize: 'Choose a size', soldOut: 'Sold out',
    unavailable: 'Unavailable for this bundle', available: 'Available',
    add: 'Add bundle to cart', checking: 'Checking your choices…', adding: 'Adding your pairs…',
    reviewCart: 'Review your cart', loadingCart: 'Checking your cart…',
    selectAll: 'Choose a print and size for pairs 1 and 2. Complete or remove any extra pairs.',
    cartLoadError: 'We couldn’t check your cart. You can choose your pairs, then try again.',
    preflightError: 'We couldn’t check the latest stock. Nothing was added. Please try again.',
    stockError: 'One or more sizes no longer have enough stock for this bundle and your cart. Please update your choices.',
    pricesChanged: 'Prices or your cart’s bundle tier have changed. Review the updated estimate, then add your bundle again.',
    partialAdd: 'Only part of your bundle was added. Review your cart before making another selection.',
    uncertainAdd: 'We couldn’t confirm the complete addition. Some pairs may be in your cart. Review your cart before trying again.',
    failedAdd: 'Your bundle could not be confirmed in the cart. Review your cart before trying again.',
    success: 'Your pairs are in the cart. Opening your cart…',
    estimateNote: 'Estimate for these {count} pairs only, with {percent}% bundle savings. Final discounts and totals are confirmed in your cart. Offers cannot be combined.',
    existingCartNote: '{count} eligible pair(s) already in your cart count towards this tier.',
    selectNote: 'Choose a print and size for each pair to see your bundle estimate. Add more pairs below.',
    unavailableError: 'The bundle builder is temporarily unavailable. Please shop individual shorts.'
  };

  const validId = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const integer = value => value !== null && value !== '' && Number.isSafeInteger(Number(value));
  const positiveQuantity = value => integer(value) && Number(value) > 0 ? Number(value) : 0;
  const MAX_PAIRS = 50;
  const format = (text, values = {}) => String(text).replace(/\{(\w+)\}/g, (match, key) => values[key] === undefined ? match : String(values[key]));

  function normalizeProducts(input) {
    if (!Array.isArray(input)) return [];
    const seenProducts = new Set();
    const seenVariants = new Set();
    return input.filter(product => product && validId(product.id) && typeof product.handle === 'string')
      .filter(product => {
        if (seenProducts.has(String(product.id))) return false;
        seenProducts.add(String(product.id));
        return true;
      }).map(product => ({
        id: String(product.id), title: String(product.title || ''), handle: product.handle,
        image: typeof product.image === 'string' ? product.image : '',
        variants: (Array.isArray(product.variants) ? product.variants : [])
          .filter(variant => variant && validId(variant.id) && integer(variant.price) && Number(variant.price) >= 0)
          .filter(variant => {
            if (seenVariants.has(String(variant.id))) return false;
            seenVariants.add(String(variant.id));
            return true;
          }).map(variant => ({
            id: String(variant.id), title: String(variant.title || ''), price: Number(variant.price),
            available: variant.available === true,
            quantity: integer(variant.quantity) ? Math.max(0, Number(variant.quantity)) : null,
            tracked: variant.tracked === true, policy: variant.policy === 'continue' ? 'continue' : 'deny'
          }))
      }));
  }

  function quantities(cart) {
    const result = new Map();
    for (const item of cart.items || []) {
      const id = String(item.variant_id || item.id);
      result.set(id, (result.get(id) || 0) + positiveQuantity(item.quantity));
    }
    return result;
  }

  function eligibleCount(cart, products) {
    const eligible = new Set(products.map(product => product.id));
    return (cart.items || []).reduce((sum, item) => sum + (eligible.has(String(item.product_id)) ? positiveQuantity(item.quantity) : 0), 0);
  }

  function selectedVariants(rows, products) {
    return rows.map(row => {
      const product = products.find(item => item.id === row.productId);
      return product && product.variants.find(variant => variant.id === row.variantId);
    });
  }

  function aggregate(rows, products) {
    const result = new Map();
    for (const variant of selectedVariants(rows, products)) {
      if (variant) result.set(variant.id, (result.get(variant.id) || 0) + 1);
    }
    return Array.from(result, ([id, quantity]) => ({ id: Number(id), quantity }));
  }

  function remaining(variant, cart, rows, excludingIndex) {
    if (!variant.available) return 0;
    if (!variant.tracked || variant.policy === 'continue' || variant.quantity === null) return Infinity;
    const selected = rows.reduce((sum, row, index) => sum + (index !== excludingIndex && row.variantId === variant.id ? 1 : 0), 0);
    return Math.max(0, variant.quantity - (quantities(cart).get(variant.id) || 0) - selected);
  }

  function selectionValid(rows, products, cart) {
    if (rows.length < 2 || rows.length > MAX_PAIRS) return false;
    return selectedVariants(rows, products).every((variant, index) => variant && remaining(variant, cart, rows, index) > 0);
  }

  function estimate(rows, products, cart) {
    if (rows.length < 2 || rows.length > MAX_PAIRS) return null;
    const selected = selectedVariants(rows, products);
    if (selected.some(variant => !variant)) return null;
    const existing = eligibleCount(cart, products);
    const percent = existing + selected.length >= 3 ? 15 : 10;
    const subtotal = selected.reduce((sum, variant) => sum + variant.price, 0);
    const saving = Math.round(subtotal * percent / 100);
    return { subtotal, saving, total: subtotal - saving, percent, existing, count: selected.length };
  }

  function eligibleCartSummary(cart, products) {
    const eligible = new Set(products.map(product => product.id));
    let quantity = 0;
    let subtotal = 0;
    let pricesKnown = true;
    let allItemsEligible = true;
    for (const item of cart.items || []) {
      const count = positiveQuantity(item.quantity);
      if (!count) continue;
      if (!eligible.has(String(item.product_id))) { allItemsEligible = false; continue; }
      quantity += count;
      if (integer(item.original_line_price) && Number(item.original_line_price) >= 0) subtotal += Number(item.original_line_price);
      else if (integer(item.original_price) && Number(item.original_price) >= 0) subtotal += Number(item.original_price) * count;
      else pricesKnown = false;
    }
    return { quantity, subtotal, pricesKnown, allItemsEligible };
  }

  function orderEstimate(rows, products, cart) {
    const selected = estimate(rows, products, cart);
    const existing = eligibleCartSummary(cart, products);
    if (!selected || !existing.quantity || !existing.pricesKnown) return null;
    const subtotal = existing.subtotal + selected.subtotal;
    const saving = Math.round(subtotal * selected.percent / 100);
    return { subtotal, saving, total: subtotal - saving, percent: selected.percent,
      count: existing.quantity + selected.count, allItemsEligible: existing.allItemsEligible };
  }

  function reconcile(before, after, requested) {
    const previous = quantities(before);
    const current = quantities(after);
    let observed = 0;
    let complete = true;
    for (const item of requested) {
      const delta = (current.get(String(item.id)) || 0) - (previous.get(String(item.id)) || 0);
      observed += Math.max(0, Math.min(item.quantity, delta));
      if (delta !== item.quantity) complete = false;
    }
    const requestedIds = new Set(requested.map(item => String(item.id)));
    for (const id of new Set([...previous.keys(), ...current.keys()])) {
      if (!requestedIds.has(id) && (previous.get(id) || 0) !== (current.get(id) || 0)) complete = false;
    }
    return { complete, observed, requested: requested.reduce((sum, item) => sum + item.quantity, 0) };
  }

  function mergeProduct(product, fresh) {
    if (!fresh || String(fresh.id) !== product.id || !Array.isArray(fresh.variants)) throw new Error('Invalid product response');
    return { ...product, variants: product.variants.map(variant => {
      const current = fresh.variants.find(item => String(item.id) === variant.id);
      if (!current) return { ...variant, available: false };
      if (!integer(current.price) || Number(current.price) < 0) throw new Error('Invalid variant price');
      return {
        ...variant, price: Number(current.price), available: current.available === true,
        quantity: integer(current.inventory_quantity) ? Math.max(0, Number(current.inventory_quantity)) : variant.quantity,
        tracked: current.inventory_management === undefined ? variant.tracked : Boolean(current.inventory_management),
        policy: current.inventory_policy === undefined ? variant.policy : (current.inventory_policy === 'continue' ? 'continue' : 'deny')
      };
    }) };
  }

  let sequence = 0;
  class MaroniBundleBuilder extends (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.strings = { ...DEFAULTS };
      this.refs = {};
      for (const key of ['rows', 'subtotal', 'saving', 'total', 'note', 'add', 'more', 'status', 'error', 'cart', 'form', 'order', 'order-heading', 'order-subtotal', 'order-saving', 'order-total', 'order-note']) {
        this.refs[key] = this.querySelector(`[data-bundle-${key}]`);
      }
      try {
        if (Object.values(this.refs).some(element => !element)) throw new Error('Incomplete markup');
        const data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
        this.products = normalizeProducts(data.products);
        for (const [key, value] of Object.entries(data.strings || {})) {
          if (typeof value === 'string' && value) this.strings[key] = value;
        }
        if (!this.products.length) throw new Error('Empty catalog');
        this.uid = `mbb-${++sequence}`;
        this.cart = { items: [] };
        this.currency = this.dataset.currency || 'AUD';
        const origin = window.location.origin;
        const sameOrigin = (value, fallback) => {
          const url = new URL(value || fallback, origin);
          if (url.origin !== origin || !/^https?:$/.test(url.protocol)) throw new Error('Invalid endpoint');
          return url.pathname.replace(/\/$/, '');
        };
        this.rootUrl = `${sameOrigin(this.dataset.root, '/')}/`;
        this.cartUrl = sameOrigin(this.dataset.cartUrl, `${this.rootUrl}cart`);
        this.cartEndpoint = `${this.cartUrl.replace(/\.js$/, '')}.js`;
        this.addEndpoint = `${sameOrigin(this.dataset.addUrl, `${this.rootUrl}cart/add`).replace(/\.js$/, '')}.js`;
        this.refs.cart.href = this.cartUrl;
        this.rows = Array.from({ length: 2 }, () => ({ productId: '', variantId: '' }));
        this.busy = false;
        this.locked = false;
        this.loadingCart = true;
        this.refs.form.addEventListener('submit', event => { event.preventDefault(); this.submit(); });
        this.refs.add.type = 'submit';
        this.refs.more.type = 'button';
        this.refs.more.addEventListener('click', () => {
          if (this.refs.more.disabled || this.busy || this.locked || this.rows.length >= MAX_PAIRS) return;
          this.rows.push({ productId: '', variantId: '' });
          this.clearError(); this.renderRows();
          this.cards[this.cards.length - 1].productSelect.focus();
        });
        this.renderRows();
        this.setStatus(this.strings.loadingCart);
        this.initialCartPromise = this.loadInitialCart();
      } catch (_) {
        if (this.refs.add) this.refs.add.disabled = true;
        this.showError(this.strings.unavailableError);
      }
    }

    renderRows() {
      this.refs.rows.replaceChildren();
      this.cards = this.rows.map((row, index) => this.createRow(row, index));
      this.update();
    }

    createRow(row, index) {
      const node = (tag, className, text) => {
        const element = document.createElement(tag);
        element.className = className;
        if (text !== undefined) element.textContent = text;
        return element;
      };
      const card = node('article', 'mbb-card');
      card.dataset.bundleRow = String(index);
      card.dataset.bundleRemovable = index >= 2 ? 'true' : 'false';
      const image = node('img', 'mbb-card__image');
      image.loading = 'lazy'; image.width = 240; image.height = 300; image.hidden = true;
      const body = node('div', 'mbb-card__body');
      const title = node('h3', 'mbb-card__title', format(this.strings.pairLabel, { number: index + 1 }));
      body.append(title);
      const field = (kind, text) => {
        const wrapper = node('div', 'mbb-field');
        const label = node('label', 'mbb-field__label', text);
        const select = node('select', 'mbb-select');
        select.id = `${this.uid}-${kind}-${index}`;
        select.dataset[kind === 'product' ? 'bundleProduct' : 'bundleVariant'] = '';
        select.required = true;
        label.htmlFor = select.id;
        wrapper.append(label, select); body.append(wrapper);
        return select;
      };
      const productSelect = field('product', this.strings.printLabel);
      const variantSelect = field('variant', this.strings.sizeLabel);
      const availability = node('p', 'mbb-card__availability');
      availability.id = `${this.uid}-availability-${index}`;
      variantSelect.setAttribute('aria-describedby', availability.id);
      body.append(availability);
      let removeButton = null;
      if (index >= 2) {
        removeButton = node('button', 'mbb-card__remove', format(this.strings.removePair, { number: index + 1 }));
        removeButton.type = 'button';
        removeButton.addEventListener('click', () => {
          if (this.busy || this.locked) return;
          this.rows.splice(index, 1);
          this.clearError(); this.renderRows(); this.refs.more.focus();
        });
        body.append(removeButton);
      }
      card.append(image, body); this.refs.rows.append(card);
      productSelect.addEventListener('change', () => {
        if (this.busy || this.locked) return;
        row.productId = productSelect.value; row.variantId = '';
        this.clearError(); this.update();
      });
      variantSelect.addEventListener('change', () => {
        if (this.busy || this.locked) return;
        row.variantId = variantSelect.value;
        this.clearError(); this.update();
      });
      return { card, image, productSelect, variantSelect, availability, removeButton };
    }

    option(value, label, disabled) {
      const option = document.createElement('option');
      option.value = value; option.textContent = label; option.disabled = Boolean(disabled);
      return option;
    }

    update() {
      this.cards.forEach((card, index) => {
        const row = this.rows[index];
        card.productSelect.replaceChildren(this.option('', this.strings.choosePrint, false));
        this.products.forEach(product => {
          const inStock = product.variants.some(variant => variant.available);
          const canSelect = product.variants.some(variant => remaining(variant, this.cart, this.rows, index) > 0);
          const suffix = !inStock ? this.strings.soldOut : (!canSelect ? this.strings.unavailable : '');
          const style = product.title.replace(/\s+(?:[-–—|]\s*)?Swim Shorts\s*$/i, '').trim() || product.title;
          card.productSelect.append(this.option(product.id, style + (suffix ? ` — ${suffix}` : ''), !canSelect));
        });
        card.productSelect.value = row.productId;
        const product = this.products.find(item => item.id === row.productId);
        card.variantSelect.replaceChildren(this.option('', this.strings.chooseSize, false));
        if (product) {
          product.variants.forEach(variant => {
            const canSelect = remaining(variant, this.cart, this.rows, index) > 0;
            const suffix = !variant.available ? this.strings.soldOut : (!canSelect ? this.strings.unavailable : '');
            card.variantSelect.append(this.option(variant.id, variant.title + (suffix ? ` — ${suffix}` : ''), !canSelect));
          });
        }
        card.variantSelect.value = row.variantId;
        card.productSelect.disabled = this.busy || this.locked;
        card.variantSelect.disabled = this.busy || this.locked || !product;
        if (card.removeButton) card.removeButton.disabled = this.busy || this.locked;
        const variant = product && product.variants.find(item => item.id === row.variantId);
        const available = variant && remaining(variant, this.cart, this.rows, index) > 0;
        card.availability.textContent = variant ? (available ? this.strings.available : this.strings.unavailable) : '';
        card.variantSelect.setAttribute('aria-invalid', variant && !available ? 'true' : 'false');
        card.image.hidden = !product || !product.image;
        if (product && product.image) {
          card.image.src = product.image;
          card.image.alt = product.title;
        } else {
          card.image.removeAttribute('src'); card.image.alt = '';
        }
      });
      const incompleteExtra = this.rows.slice(2).some(row => !row.productId || !row.variantId);
      const value = estimate(this.rows, this.products, this.cart);
      for (const key of ['subtotal', 'saving', 'total']) this.refs[key].textContent = value ? this.money(value[key]) : '—';
      this.refs.note.textContent = value
        ? `${format(this.strings.estimateNote, value)}${value.existing ? ` ${format(this.strings.existingCartNote, { count: value.existing })}` : ''}`
        : (incompleteExtra ? this.strings.selectAll : this.strings.selectNote);
      const valid = selectionValid(this.rows, this.products, this.cart);
      const availablePrices = new Set(this.products.flatMap(product => product.variants
        .filter(variant => remaining(variant, this.cart, this.rows, -1) > 0).map(variant => variant.price)));
      const moreStock = availablePrices.size > 0;
      const existing = eligibleCartSummary(this.cart, this.products);
      const thirdPair = existing.quantity + this.rows.length === 2;
      let moreLabel = thirdPair ? this.strings.addThird : this.strings.addAnother;
      if (valid && value && availablePrices.size === 1 && existing.pricesKnown) {
        const saving = Math.round((existing.subtotal + value.subtotal + [...availablePrices][0]) * 15 / 100);
        moreLabel = format(thirdPair ? this.strings.addThirdSaving : this.strings.addNextSaving, { saving: this.money(saving) });
      }
      this.refs.more.disabled = this.busy || this.loadingCart || this.locked || !valid || !moreStock || this.rows.length >= MAX_PAIRS;
      this.refs.more.textContent = this.rows.length >= MAX_PAIRS ? this.strings.maximumPairs
        : (!valid ? this.strings.completePairs : (!moreStock ? this.strings.noMoreStock
          : moreLabel));
      const whole = orderEstimate(this.rows, this.products, this.cart);
      this.refs.order.hidden = !existing.quantity;
      this.refs['order-heading'].textContent = existing.allItemsEligible ? this.strings.orderHeading : this.strings.eligibleHeading;
      for (const key of ['subtotal', 'saving', 'total']) this.refs[`order-${key}`].textContent = whole ? this.money(whole[key]) : '—';
      this.refs['order-note'].textContent = `${whole ? this.strings.orderNote : this.strings.orderEstimateUnavailable}${!existing.allItemsEligible ? ` ${this.strings.excludedNote}` : ''}`;
      this.refs.add.disabled = this.busy || this.loadingCart || (!this.locked && !valid);
      this.refs.add.textContent = this.locked ? this.strings.reviewCart : (this.busy ? this.strings.checking : this.strings.add);
      this.refs.form.setAttribute('aria-busy', this.busy || this.loadingCart ? 'true' : 'false');
    }

    money(cents) {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: this.currency, currencyDisplay: 'code' }).format(cents / 100); }
      catch (_) { return `${this.currency} ${(cents / 100).toFixed(2)}`; }
    }

    setStatus(message) { if (this.refs.status) this.refs.status.textContent = message; }
    clearError() {
      if (this.refs.error) { this.refs.error.textContent = ''; this.refs.error.hidden = true; }
      this.setStatus('');
    }
    showError(message) {
      if (this.refs.error) { this.refs.error.textContent = message; this.refs.error.hidden = false; }
    }

    async request(url, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, {
          credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal,
          headers: { Accept: 'application/json', ...(options.headers || {}) }
        });
        const data = await response.json();
        if (!response.ok || (data && Number(data.status) >= 400)) {
          const error = new Error('Shopify request failed');
          error.status = response.status; throw error;
        }
        return data;
      } finally { clearTimeout(timeout); }
    }

    async readCart() {
      const cart = await this.request(this.cartEndpoint);
      if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
      return cart;
    }

    useCart(cart) {
      this.cart = cart;
      if (typeof cart.currency === 'string' && /^[A-Z]{3}$/.test(cart.currency)) this.currency = cart.currency;
    }

    async loadInitialCart() {
      try { this.useCart(await this.readCart()); this.setStatus(''); }
      catch (_) { this.showError(this.strings.cartLoadError); this.setStatus(''); }
      finally { this.loadingCart = false; this.update(); }
    }

    async refreshSelectedProducts() {
      const selectedIds = new Set(this.rows.map(row => row.productId));
      const refreshed = await Promise.all(this.products.filter(product => selectedIds.has(product.id)).map(async product => {
        const fresh = await this.request(`${this.rootUrl}products/${encodeURIComponent(product.handle)}.js`);
        return mergeProduct(product, fresh);
      }));
      this.products = this.products.map(product => refreshed.find(item => item.id === product.id) || product);
    }

    estimateSignature() {
      const value = estimate(this.rows, this.products, this.cart);
      const existing = eligibleCartSummary(this.cart, this.products);
      return value ? `${this.currency}:${value.subtotal}:${value.saving}:${value.percent}:${existing.quantity}:${existing.subtotal}:${existing.pricesKnown}` : '';
    }

    goToCart() { window.location.assign(this.cartUrl); }

    async submit() {
      if (this.busy || this.loadingCart) return;
      if (this.locked) { this.goToCart(); return; }
      if (!selectionValid(this.rows, this.products, this.cart)) { this.showError(this.strings.selectAll); return; }
      this.clearError();
      const displayedEstimate = this.estimateSignature();
      this.busy = true; this.update(); this.setStatus(this.strings.checking);
      let before;
      try {
        before = await this.readCart();
        this.useCart(before);
        await this.refreshSelectedProducts();
        this.update();
        if (!selectionValid(this.rows, this.products, this.cart)) {
          this.showError(this.strings.stockError); return;
        }
        if (displayedEstimate !== this.estimateSignature()) {
          this.showError(this.strings.pricesChanged); return;
        }
      } catch (_) {
        this.showError(this.strings.preflightError); return;
      } finally {
        if (!this.refs.error.hidden) { this.busy = false; this.update(); this.setStatus(''); }
      }

      const items = aggregate(this.rows, this.products);
      this.locked = true;
      this.refs.cart.hidden = false;
      this.setStatus(this.strings.adding);
      this.refs.add.textContent = this.strings.adding;
      let addSucceeded = false;
      try {
        await this.request(this.addEndpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items })
        });
        addSucceeded = true;
      } catch (_) {  }

      let result = null;
      try {
        const after = await this.readCart();
        result = reconcile(before, after, items);
        this.useCart(after);
      } catch (_) {  }
      this.busy = false;
      if (addSucceeded && result && result.complete) {
        this.rows.forEach(row => { row.productId = ''; row.variantId = ''; });
        this.rows.splice(2);
        this.locked = false;
        this.renderRows(); this.setStatus(this.strings.success);
        this.goToCart();
        return;
      }
      this.update(); this.setStatus('');
      this.showError(result && result.observed > 0 && result.observed < result.requested
        ? this.strings.partialAdd
        : (result && result.observed === 0 && addSucceeded ? this.strings.failedAdd : this.strings.uncertainAdd));
      this.refs.cart.focus();
    }
  }

  if (typeof customElements !== 'undefined' && !customElements.get('maroni-bundle-builder')) {
    customElements.define('maroni-bundle-builder', MaroniBundleBuilder);
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { MaroniBundleBuilder, DEFAULTS, MAX_PAIRS, normalizeProducts, quantities, eligibleCount, eligibleCartSummary, orderEstimate, selectedVariants, aggregate, remaining, selectionValid, estimate, reconcile, mergeProduct };
  }
})();
